/**
 * Subscription Form Component
 * Form for creating and editing recurring billing subscriptions
 */

import React, { useState, useEffect } from 'react';
import {
  createSubscription,
  updateSubscription,
  getSubscription,
} from '../services/subscriptionService';
import { getClients } from '../services/clientService';
import { getEmailTemplates } from '../services/emailTemplateService';
import { getPaymentGatewayConfigs, PAYMENT_GATEWAYS } from '../services/paymentGatewayService';

const SubscriptionForm = ({ userId, subscriptionId = null, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [paymentGateways, setPaymentGateways] = useState([]);
  
  const [formData, setFormData] = useState({
    clientId: '',
    amount: '',
    currency: 'USD',
    description: '',
    frequency: 'monthly',
    billingDay: 1,
    emailTemplateId: 'default',
    sendEmailOnGeneration: true,
    autoSend: true,
    paymentGateway: 'manual',
    startDate: new Date().toISOString().split('T')[0],
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, [userId, subscriptionId]);

  const loadData = async () => {
    try {
      // Load clients
      const clientsData = await getClients(userId);
      setClients(clientsData);

      // Load email templates
      const templatesData = await getEmailTemplates(userId);
      setEmailTemplates(templatesData);

      // Load payment gateways
      const gatewaysData = await getPaymentGatewayConfigs(userId);
      setPaymentGateways(gatewaysData);

      // Load subscription if editing
      if (subscriptionId) {
        const subscription = await getSubscription(subscriptionId);
        setFormData({
          clientId: subscription.clientId,
          amount: subscription.amount,
          currency: subscription.currency,
          description: subscription.description,
          frequency: subscription.frequency,
          billingDay: subscription.billingDay,
          emailTemplateId: subscription.emailTemplateId,
          sendEmailOnGeneration: subscription.sendEmailOnGeneration,
          autoSend: subscription.autoSend,
          paymentGateway: subscription.paymentGateway,
          startDate: new Date(subscription.startDate).toISOString().split('T')[0],
          notes: subscription.notes,
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load form data');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const selectedClient = clients.find(c => c.id === formData.clientId);
      if (!selectedClient) {
        throw new Error('Please select a client');
      }

      const subscriptionData = {
        ...formData,
        clientName: selectedClient.name,
        clientEmail: selectedClient.email,
        amount: parseFloat(formData.amount),
        billingDay: parseInt(formData.billingDay),
        startDate: new Date(formData.startDate),
      };

      if (subscriptionId) {
        await updateSubscription(subscriptionId, subscriptionData);
      } else {
        await createSubscription(userId, subscriptionData);
      }

      alert(subscriptionId ? 'Subscription updated!' : 'Subscription created!');
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error saving subscription:', error);
      alert('Failed to save subscription: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="subscription-form">
      {/* Client Selection */}
      <div className="form-group">
        <label htmlFor="clientId">Client *</label>
        <select
          id="clientId"
          name="clientId"
          value={formData.clientId}
          onChange={handleChange}
          required
        >
          <option value="">Select a client</option>
          {clients.map(client => (
            <option key={client.id} value={client.id}>
              {client.name} ({client.email})
            </option>
          ))}
        </select>
      </div>

      {/* Amount */}
      <div className="form-group">
        <label htmlFor="amount">Amount *</label>
        <input
          type="number"
          id="amount"
          name="amount"
          value={formData.amount}
          onChange={handleChange}
          min="0"
          step="0.01"
          required
        />
      </div>

      {/* Currency */}
      <div className="form-group">
        <label htmlFor="currency">Currency</label>
        <select
          id="currency"
          name="currency"
          value={formData.currency}
          onChange={handleChange}
        >
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="GBP">GBP</option>
          <option value="PHP">PHP</option>
          <option value="SGD">SGD</option>
        </select>
      </div>

      {/* Description */}
      <div className="form-group">
        <label htmlFor="description">Description</label>
        <input
          type="text"
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="e.g., Monthly hosting service"
        />
      </div>

      {/* Frequency */}
      <div className="form-group">
        <label htmlFor="frequency">Billing Frequency *</label>
        <select
          id="frequency"
          name="frequency"
          value={formData.frequency}
          onChange={handleChange}
          required
        >
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>

      {/* Billing Day (for monthly/quarterly/yearly) */}
      {['monthly', 'quarterly', 'yearly'].includes(formData.frequency) && (
        <div className="form-group">
          <label htmlFor="billingDay">Billing Day of Month</label>
          <input
            type="number"
            id="billingDay"
            name="billingDay"
            value={formData.billingDay}
            onChange={handleChange}
            min="1"
            max="31"
          />
          <small>Day of the month to generate invoice (1-31)</small>
        </div>
      )}

      {/* Start Date */}
      <div className="form-group">
        <label htmlFor="startDate">Start Date</label>
        <input
          type="date"
          id="startDate"
          name="startDate"
          value={formData.startDate}
          onChange={handleChange}
        />
      </div>

      {/* Payment Gateway */}
      <div className="form-group">
        <label htmlFor="paymentGateway">Payment Gateway</label>
        <select
          id="paymentGateway"
          name="paymentGateway"
          value={formData.paymentGateway}
          onChange={handleChange}
        >
          <option value="manual">Manual Payment</option>
          {paymentGateways.filter(g => g.isActive).map(gateway => (
            <option key={gateway.id} value={gateway.gateway}>
              {gateway.displayName || gateway.gateway}
            </option>
          ))}
        </select>
      </div>

      {/* Email Template */}
      <div className="form-group">
        <label htmlFor="emailTemplateId">Email Template</label>
        <select
          id="emailTemplateId"
          name="emailTemplateId"
          value={formData.emailTemplateId}
          onChange={handleChange}
        >
          <option value="default">Default Template</option>
          {emailTemplates.map(template => (
            <option key={template.id} value={template.id}>
              {template.name}
            </option>
          ))}
        </select>
      </div>

      {/* Email Settings */}
      <div className="form-group">
        <label>
          <input
            type="checkbox"
            name="sendEmailOnGeneration"
            checked={formData.sendEmailOnGeneration}
            onChange={handleChange}
          />
          Send email when invoice is generated
        </label>
      </div>

      <div className="form-group">
        <label>
          <input
            type="checkbox"
            name="autoSend"
            checked={formData.autoSend}
            onChange={handleChange}
          />
          Automatically send invoices (no manual approval)
        </label>
      </div>

      {/* Notes */}
      <div className="form-group">
        <label htmlFor="notes">Notes</label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows="3"
          placeholder="Additional notes for the invoice"
        />
      </div>

      {/* Action Buttons */}
      <div className="form-actions">
        <button type="submit" disabled={loading}>
          {loading ? 'Saving...' : subscriptionId ? 'Update Subscription' : 'Create Subscription'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default SubscriptionForm;
